/**
 * WorkerAdvisor — ИИ-советник на Дашборде Работника (роль worker).
 *
 * Симметрия с AdminAdvisor, но валюта другая: не деньги, а ПОЛНОТА ИСПОЛНЕНИЯ
 * (фото результата) + СВОЕВРЕМЕННОСТЬ. Гибрид: цифры из GET /api/tasks/worker-advisor
 * (детерминированный SQL), ИИ-строка `line` приходит в состоянии live. Состояния:
 *   cold    — ничего не рисуем (онбординг закрывает <OnboardingChecklist> на странице);
 *   warming — есть работы, но ни одной закрытой → мост к первой закрытой с фото;
 *   live    — ИИ-строка + тайлы + карточки-действия.
 *
 * CTA каждой карточки ЯВНО называет конкретную Работу и ведёт прямо на неё
 * (deep-link в Объект с раскрытой Работой). Все строки — через t() (i18n).
 * PostHog: показ, клики по тайлам/карточкам/числам строки.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { Analytics } from '../analytics';

// Русское склонение по числу: _one/_few/_many (учитывает 11–14 и десятки). EN: one/много.
function plf(n, lang) {
  if (lang === 'en') return n === 1 ? 'one' : 'many';
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'many';
  if (m10 === 1) return 'one';
  if (m10 >= 2 && m10 <= 4) return 'few';
  return 'many';
}

const TONE = {
  danger: 'var(--red, #a32d2d)',
  amber:  'var(--orange, #854f0b)',
  info:   'var(--accent, #185fa5)',
  teal:   'var(--green, #0f6e56)',
  gray:   'var(--text2, #5f5e5a)',
};

const PATHS = {
  sparkles: 'M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9zM18 14l1 2.4 2.4 1-2.4 1-1 2.4-1-2.4-2.4-1 2.4-1z',
  alert:    'M12 4l9 16H3zM12 10v4M12 17.5v.5',
  camera:   'M3 7h4l2-2h6l2 2h4v12H3zM12 11a3 3 0 100 6 3 3 0 000-6z',
  clock:    'M12 7v5l3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  bell:     'M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0',
  check:    'M20 6L9 17l-5-5',
};

function Ico({ name, size = 19 }) {
  const d = PATHS[name] || PATHS.alert;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {d.split('M').filter(Boolean).map((seg, i) => <path key={i} d={'M' + seg} />)}
    </svg>
  );
}

function Card({ tone, ico, title, sub, cta, phId, onClick }) {
  const color = TONE[tone] || TONE.gray;
  return (
    <div data-ph-id={phId} style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 10,
    }}>
      <div style={{
        width: 38, height: 38, flexShrink: 0, borderRadius: '50%',
        background: 'var(--bg3)', color, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}><Ico name={ico} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 2, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
      </div>
      {cta && (
        <button className="btn btn-secondary btn-sm" onClick={onClick}
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{cta}</button>
      )}
    </div>
  );
}

// ── linkify числа в ИИ-строке (deep-link «к сути»), без lookbehind (Safari) ──
function escapeRe(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function numVariants(value) {
  const raw = String(value);
  return Array.from(new Set([
    raw,
    Number(value).toLocaleString('ru-RU'),
    Number(value).toLocaleString('en-US'),
  ])).filter(Boolean);
}
function linkifySegments(line, targets) {
  const matches = [];
  targets.forEach((tg) => {
    if (!tg || tg.value == null) return;
    numVariants(tg.value).forEach((v) => {
      const re = new RegExp(escapeRe(v), 'g');
      let m;
      while ((m = re.exec(line)) !== null) {
        const before = line[m.index - 1];
        const after = line[m.index + v.length];
        if ((before && /\d/.test(before)) || (after && /\d/.test(after))) continue;
        matches.push({ start: m.index, end: m.index + v.length, key: tg.key, onClick: tg.onClick });
      }
    });
  });
  matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const usedKey = new Set();
  const chosen = [];
  let lastEnd = -1;
  for (const m of matches) {
    if (m.start < lastEnd || usedKey.has(m.key)) continue;
    chosen.push(m); usedKey.add(m.key); lastEnd = m.end;
  }
  if (!chosen.length) return line;
  const segs = [];
  let i = 0;
  chosen.forEach((m, idx) => {
    if (m.start > i) segs.push(line.slice(i, m.start));
    segs.push(
      <button key={`n${idx}`} type="button" onClick={m.onClick}
        data-ph-id={`worker-advisor-line-${m.key}`}
        style={{
          font: 'inherit', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer',
          background: 'none', border: 'none', padding: 0, margin: 0,
          textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2,
        }}>{line.slice(m.start, m.end)}</button>
    );
    i = m.end;
  });
  if (i < line.length) segs.push(line.slice(i));
  return segs;
}

const ADV_CSS = `
.wadv-spark{ display:inline-flex; animation: wadv-pulse 1.4s ease-in-out infinite; }
@keyframes wadv-pulse{ 0%,100%{ opacity:.55; transform:scale(1); } 50%{ opacity:1; transform:scale(1.14); } }
.wadv-mat{ animation: wadv-m .55s ease-out both; }
@keyframes wadv-m{ from{ opacity:0; filter:blur(6px); transform:translateY(2px); } to{ opacity:1; filter:blur(0); transform:none; } }
.wadv-bar{ height:9px; border-radius:5px; background:linear-gradient(90deg, var(--border,#e0e0e8) 25%, var(--bg2,#f0f0f5) 50%, var(--border,#e0e0e8) 75%); background-size:300px 100%; animation: wadv-shim 1.3s linear infinite; }
@keyframes wadv-shim{ 0%{ background-position:-150px 0; } 100%{ background-position:150px 0; } }
`;

function AdvisorStrip({ subtitle, text, loading }) {
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start',
      background: 'rgba(79,142,247,0.06)', border: '1px solid rgba(79,142,247,0.3)',
      borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 14,
    }}>
      <style>{ADV_CSS}</style>
      <span className={loading && !text ? 'wadv-spark' : undefined} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>
        <Ico name="sparkles" size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 3 }}>
          {subtitle}
        </div>
        {text ? (
          <div className="wadv-mat" style={{ fontSize: 15, lineHeight: 1.4, color: 'var(--text)' }}>{text}</div>
        ) : loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
            <span className="wadv-bar" style={{ width: '92%' }} />
            <span className="wadv-bar" style={{ width: '58%' }} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Маленький тайл-фильтр (Сегодня · Просрочено · Без фото).
function Tile({ label, value, accent, phId, onClick }) {
  return (
    <button onClick={onClick} data-ph-id={phId} style={{
      textAlign: 'center', cursor: 'pointer', border: '1px solid var(--border)',
      background: 'var(--bg2)', borderRadius: 'var(--radius-lg)', padding: '12px 8px',
    }}>
      <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', color: accent || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 2 }}>{label}</div>
    </button>
  );
}

export default function WorkerAdvisor({ data, lineLoading = false }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const loaded = data != null;

  useEffect(() => {
    if (loaded && data && data.state && data.state !== 'cold') {
      Analytics.workerAdvisorViewed(data.state);
    }
  }, [loaded, data]);

  if (!loaded || !data || !data.state || data.state === 'cold') return null;

  const s = data.signals || {};
  const state = data.state;

  // Открыть конкретную Работу раскрытой в её Объекте (deep-link «к сути»).
  const openWork = (task) => {
    if (task && task.property_id) {
      navigate(`/properties/${task.property_id}`, { state: { openTaskId: task.id, from: '/dashboard' } });
    } else {
      navigate('/tasks');
    }
  };
  const goTasks = (returnFilter) => navigate('/tasks', returnFilter ? { state: { returnFilter } } : undefined);
  // Drill-down фильтры (видимый чип слева над списком Работ, как у Админа).
  const goNoPhoto = () => navigate('/tasks', { state: { noPhotoOnly: true } });
  const goNew = () => navigate('/tasks', { state: { newOnly: true } });
  const workLabel = (task) => task ? `${task.title} · ${task.property_name}` : '';

  // ── WARMING: мост к первой закрытой работе с фото ──
  if (state === 'warming') {
    const first = s.today?.nearest || s.overdue?.tasks?.[0] || s.newAssignments?.tasks?.[0] || null;
    return (
      <div style={{ padding: '0 16px', marginTop: 12 }}>
        <AdvisorStrip subtitle={t('workerAdvisor.label')} text={t('workerAdvisor.warming.line')} />
        <Card tone="info" ico="check" phId="worker-advisor-warming-first"
          title={t('workerAdvisor.warming.firstTitle')}
          sub={first ? workLabel(first) : t('workerAdvisor.warming.firstSubGeneric')}
          cta={first ? t('workerAdvisor.warming.firstCtaWork') : t('workerAdvisor.warming.firstCtaList')}
          onClick={() => { Analytics.workerAdvisorCardClicked('warming_first', state); first ? openWork(first) : goTasks(); }} />
      </div>
    );
  }

  // ── LIVE ──
  const counts = s.counts || {};
  const overdue = s.overdue || { count: 0, tasks: [] };
  const noPhoto = s.noPhoto || { count: 0, tasks: [] };
  const newAssign = s.newAssignments || { count: 0, tasks: [] };
  const nearest = s.today?.nearest || null;

  const lineTargets = [
    { key: 'overdue', value: counts.overdue > 0 ? counts.overdue : null,
      onClick: () => { Analytics.workerAdvisorLineClicked('overdue', state); goTasks('overdue'); } },
    { key: 'today', value: counts.today > 0 ? counts.today : null,
      onClick: () => { Analytics.workerAdvisorLineClicked('today', state); nearest ? openWork(nearest) : goTasks(); } },
    { key: 'no_photo', value: counts.no_photo > 0 ? counts.no_photo : null,
      onClick: () => { Analytics.workerAdvisorLineClicked('no_photo', state); goNoPhoto(); } },
    { key: 'new_assignments', value: counts.new_assignments > 0 ? counts.new_assignments : null,
      onClick: () => { Analytics.workerAdvisorLineClicked('new_assignments', state); goNew(); } },
  ];
  const lineNode = data.line ? linkifySegments(data.line, lineTargets) : null;

  return (
    <div style={{ padding: '0 16px', marginTop: 12 }} data-ph-id="worker-advisor-live">
      {(lineNode || lineLoading) && (
        <AdvisorStrip subtitle={t('workerAdvisor.liveLabel')} text={lineNode} loading={lineLoading} />
      )}

      {/* Тайлы-фильтры */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        <Tile label={t('workerAdvisor.tiles.today')} value={counts.today || 0} accent="var(--accent)"
          phId="worker-advisor-tile-today"
          onClick={() => { Analytics.workerAdvisorMetricClicked('today', state); nearest ? openWork(nearest) : goTasks(); }} />
        <Tile label={t('workerAdvisor.tiles.overdue')} value={counts.overdue || 0} accent="var(--red)"
          phId="worker-advisor-tile-overdue"
          onClick={() => { Analytics.workerAdvisorMetricClicked('overdue', state); goTasks('overdue'); }} />
        <Tile label={t('workerAdvisor.tiles.noPhoto')} value={counts.no_photo || 0} accent="var(--orange)"
          phId="worker-advisor-tile-no-photo"
          onClick={() => { Analytics.workerAdvisorMetricClicked('no_photo', state); goNoPhoto(); }} />
      </div>

      {/* Карточки-действия — CTA явно называет конкретную Работу */}
      {overdue.count > 0 && (
        <Card tone="danger" ico="alert" phId="worker-advisor-card-overdue"
          title={t(`workerAdvisor.overdue.title_${plf(overdue.count, i18n.language)}`, { count: overdue.count })}
          sub={overdue.count === 1 ? workLabel(overdue.tasks[0]) : t('workerAdvisor.overdue.subStart', { work: workLabel(overdue.tasks[0]) })}
          cta={t('workerAdvisor.overdue.cta')}
          onClick={() => { Analytics.workerAdvisorCardClicked('overdue', state); goTasks('overdue'); }} />
      )}
      {noPhoto.count > 0 && (
        <Card tone="amber" ico="camera" phId="worker-advisor-card-no-photo"
          title={t(`workerAdvisor.noPhoto.title_${plf(noPhoto.count, i18n.language)}`, { count: noPhoto.count })}
          sub={noPhoto.count === 1 ? workLabel(noPhoto.tasks[0]) : t('workerAdvisor.noPhoto.subStart', { work: workLabel(noPhoto.tasks[0]) })}
          cta={t('workerAdvisor.noPhoto.cta')}
          onClick={() => { Analytics.workerAdvisorCardClicked('no_photo', state); goNoPhoto(); }} />
      )}
      {newAssign.count > 0 && (
        <Card tone="info" ico="bell" phId="worker-advisor-card-new"
          title={t(`workerAdvisor.newAssignments.title_${plf(newAssign.count, i18n.language)}`, { count: newAssign.count })}
          sub={newAssign.count === 1 ? workLabel(newAssign.tasks[0]) : t('workerAdvisor.newAssignments.subStart', { work: workLabel(newAssign.tasks[0]) })}
          cta={t('workerAdvisor.newAssignments.cta')}
          onClick={() => { Analytics.workerAdvisorCardClicked('new_assignments', state); goNew(); }} />
      )}
    </div>
  );
}
